import * as Yup from 'yup';
import Grid from '@mui/material/Grid';
import Typography from '../../../componentsv2/Typography';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Chip from '../../../componentsv2/Chip';
import Section from '../../../componentsv2/Section';
import { COLORS } from '../../../themev2';
import Button from '../../../componentsv2/Button';
import { useEffect, useState } from 'react';
import { Company, CompanyUser, CompanyUserInvitation, UserRole } from '../../../types/companyUser';
import {
  deleteInvitation,
  deleteMember,
  editMember,
  getCompanyUserInvitations,
  inviteUser,
  resendInvitation,
} from '../../../services/companyUsers';
import { useTranslation } from '../../../hooks/i18n';
import Modal from '../../../componentsv2/Modal';
import { RadioField, TextField } from '../../../componentsv2/Field';
import { Formik } from 'formik';
import Form from '../../../componentsv2/Form';
import { useSnackbar } from '../../../context';
import { AxiosError } from 'axios';
import { CircularProgress } from '@mui/material';
import MoreMenu from '../../../componentsv2/MoreMenu';
import { logEvent } from '@amplitude/analytics-browser';
import { userInviteEvents } from '../../../constants/events';
import InfoDrawer from '../../../componentsv2/InfoDrawer';
import {
  adminDeleteCompanyUser,
  adminDeleteInvitation,
  adminEditCompanyUserRole,
  adminGetUserInvitations,
  adminInviteUser,
  adminResendInvitation,
  adminResendPasswordEmail,
} from '../../../services/admin';

type Member = {
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  isPending: boolean;
  invitationId?: number;
  userRoleId?: number;
  jobTitle: string;
  needsPasswordSetup?: boolean;
};

function orderMembers(members: Member[], currentCompanyUser?: CompanyUser) {
  const currentCompanyUserIndex = members.findIndex(
    ({ firstName, lastName }) =>
      firstName === currentCompanyUser?.user.firstName && lastName === currentCompanyUser?.user.lastName
  );
  if (currentCompanyUserIndex !== -1) {
    const [currentCompanyUser] = members.splice(currentCompanyUserIndex, 1);
    members.unshift(currentCompanyUser);
  }
  return members;
}

function transformInvitation({
  userFirstName,
  userLastName,
  userEmail,
  role,
  id,
  jobTitle,
}: CompanyUserInvitation): Member {
  return {
    firstName: userFirstName,
    lastName: userLastName,
    email: userEmail,
    role,
    isPending: true,
    invitationId: id,
    jobTitle,
  };
}

type UserInvitesProps = {
  company: Company;
  currentCompanyUser?: CompanyUser;
  isCompanyAdministrator: boolean;
};

const UserInvites = ({ company, currentCompanyUser, isCompanyAdministrator }: UserInvitesProps) => {
  const { t } = useTranslation(['myAccount', 'user']);
  const [members, setMembers] = useState<Member[]>([]);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [currentlyEditing, setCurrentlyEditing] = useState<Member | null>(null);
  const [currentlyDeleting, setCurrentlyDeleting] = useState<Member | null>(null);
  const [isLoadingInvite, setIsLoadingInvite] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [isDeleting, setIsDeleting] = useState(false);
  const companyId = company?.id;

  function loadMembers(companyId: number): Promise<void> {
    setIsLoadingMembers(true);
    const _getInvitations = !!currentCompanyUser ? getCompanyUserInvitations : adminGetUserInvitations;
    return _getInvitations(companyId)
      .then((invitations) => {
        setMembers(() => [
          ...company.companyUserRoles.map(
            ({
              user: {
                user: { email, firstName, lastName, needsPasswordSetup },
              },
              role,
              id,
              jobTitle,
            }) => ({
              firstName,
              lastName,
              email,
              role,
              isPending: false,
              userRoleId: id,
              jobTitle,
              needsPasswordSetup,
            })
          ),
          ...invitations.map((invitation) => transformInvitation(invitation)),
        ]);
      })
      .finally(() => {
        setIsLoadingMembers(false);
      });
  }

  function removeMember(memberEmail: string) {
    setMembers((prevState) => {
      const memberIndex = prevState.findIndex(({ email }) => email === memberEmail);
      if (memberIndex !== -1) {
        const updatedMembers = [...prevState];
        updatedMembers.splice(memberIndex, 1);
        return updatedMembers;
      }
      return prevState;
    });
  }

  function getMenuActions(member: Member): { label: string; onClick: () => void }[] {
    const { invitationId, isPending, email, role, needsPasswordSetup } = member;
    // if it's me, allow to change job title
    if (email === currentCompanyUser?.user?.email) {
      return [
        {
          label: t(`myAccount:users.actions.edit`),
          onClick: () => {
            setCurrentlyEditing(member);
            setIsFormModalOpen(true);
          },
        },
      ];
    }

    if (!isCompanyAdministrator)
      // if it's not an admin, return no actions
      return [];

    // if the user is pending, show resend and delete invitation
    if (isPending)
      return [
        {
          label: t(`myAccount:users.actions.resend`),
          onClick: () => {
            const _resendInvitation = !!currentCompanyUser ? resendInvitation : adminResendInvitation;
            _resendInvitation(companyId, invitationId!).then(() => {
              logEvent(userInviteEvents.RESEND_INVITE);
              showSnackbar(t('myAccount:users.createSuccess'), 'success');
            });
          },
        },
        {
          label: t(`myAccount:users.actions.deleteInvite`),
          onClick: () => {
            const _deleteInvitation = !!currentCompanyUser ? deleteInvitation : adminDeleteInvitation;
            _deleteInvitation(companyId, invitationId!).then(() => {
              logEvent(userInviteEvents.DELETE_INVITE);
              showSnackbar(t('myAccount:users.deleteSuccess'), 'success');
              removeMember(email);
            });
          },
        },
      ];

    // if the user is owner, it can't be edited by anyone else.
    if (role === 'owner' && !!currentCompanyUser) {
      return [];
    }

    // if the user is not pending, show edit and delete
    return [
      {
        label: t(`myAccount:users.actions.edit`),
        onClick: () => {
          setCurrentlyEditing(member);
          setIsFormModalOpen(true);
        },
      },
      // Only from the admin portal, and only while the user has not set a password yet
      ...(currentCompanyUser || !needsPasswordSetup
        ? []
        : [
            {
              label: t('myAccount:users.actions.resendPasswordEmail'),
              onClick: () => {
                adminResendPasswordEmail(companyId, email)
                  .then(() => showSnackbar(t('myAccount:users.resendPasswordEmailSuccess'), 'success'))
                  .catch(() => showSnackbar(t('myAccount:users.resendPasswordEmailError'), 'error'));
              },
            },
          ]),
      {
        label: t(`myAccount:users.actions.delete`),
        onClick: () => {
          setCurrentlyDeleting(member);
        },
      },
    ];
  }

  useEffect(() => {
    logEvent(userInviteEvents.VIEW_PAGE);
    loadMembers(companyId);
  }, [company, companyId]);

  return (
    <>
      <Section>
        <Grid container alignItems="center">
          <Grid item xs={12} md>
            <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>{t('myAccount:users.title')}</Typography>
          </Grid>
          {isCompanyAdministrator && (
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                <Button
                  onClick={() => {
                    setIsFormModalOpen(true);
                  }}
                >
                  {t('myAccount:users.addBtn')}
                </Button>
              </Box>
            </Grid>
          )}
          {isLoadingMembers && (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Grid>
          )}
          {orderMembers(members, currentCompanyUser).map((member, index) => {
            const { firstName, lastName, email, role, isPending, jobTitle } = member;
            const menuActions = getMenuActions(member);

            return (
              <Grid item xs={12} key={email}>
                <Grid container alignItems="center">
                  <Grid item xs={4}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Avatar
                        sx={{ backgroundColor: COLORS.gray[1100], display: { xs: 'none', lg: 'flex' } }}
                      >
                        {firstName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          paddingLeft: '1em',
                          overflow: 'hidden',
                          maxWidth: '100%',
                        }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          {`${firstName} ${lastName}`}{' '}
                          {email === currentCompanyUser?.user?.email && `(${t('myAccount:users.you')})`}
                        </Typography>
                        <Typography
                          sx={{
                            color: COLORS.gray[1100],
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={3} textAlign="center">
                    {jobTitle && <Typography>{jobTitle}</Typography>}
                  </Grid>

                  <Grid item xs={2} textAlign="center">
                    {isPending && <Typography>{t(`myAccount:users.invited`)}</Typography>}
                  </Grid>

                  <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip label={t(`myAccount:users.roles.${role}`)} />
                  </Grid>
                  <Grid item xs={1}>
                    {menuActions.length > 0 && <MoreMenu items={menuActions} />}
                  </Grid>
                  {index !== members.length - 1 && (
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Section>
      <Formik
        key={currentlyEditing?.email}
        initialValues={{
          userFirstName: currentlyEditing?.firstName || '',
          userLastName: currentlyEditing?.lastName || '',
          userEmail: currentlyEditing?.email || '',
          role: (currentlyEditing?.role || '') as UserRole,
          jobTitle: currentlyEditing?.jobTitle || '',
        }}
        validationSchema={Yup.object().shape({
          userFirstName: currentlyEditing ? Yup.string() : Yup.string().required(t('common:requiredField')),
          userLastName: currentlyEditing ? Yup.string() : Yup.string().required(t('common:requiredField')),
          userEmail: currentlyEditing ? Yup.string() : Yup.string().required(t('common:requiredField')),
          role: Yup.string().required(t('common:requiredField')),
          jobTitle: Yup.string(),
        })}
        onSubmit={(values) => {
          setIsLoadingInvite(true);
          const _editMember = !!currentCompanyUser ? editMember : adminEditCompanyUserRole;
          const _inviteUser = !!currentCompanyUser ? inviteUser : adminInviteUser;
          const result = currentlyEditing
            ? _editMember(companyId, currentlyEditing.userRoleId!, values)
            : _inviteUser(companyId, values);
          result
            .then((result) => {
              const event = currentlyEditing ? userInviteEvents.EDIT_INVITE : userInviteEvents.FORM_SUBMITTED;

              setCurrentlyEditing(null);
              logEvent(event, values);

              if (currentlyEditing) {
                // Edit existing user
                setMembers((prevState) => {
                  const memberIndex = prevState.findIndex(({ email }) => email === currentlyEditing?.email);
                  if (memberIndex !== -1) {
                    const updatedMembers = [...prevState];
                    updatedMembers[memberIndex] = {
                      ...updatedMembers[memberIndex],
                      role: values.role,
                      jobTitle: values.jobTitle,
                    };
                    return updatedMembers;
                  }
                  return prevState;
                });
              } else {
                // Add invitation as new member
                setMembers((prevState) => [...prevState, transformInvitation(result)]);
              }

              const successMessage = currentlyEditing ? 'users.editSuccess' : 'users.createSuccess';
              showSnackbar(t(successMessage), 'success');
              setIsFormModalOpen(false);
              setCurrentlyEditing(null);
            })
            .catch((error: AxiosError) => {
              const errorCode = error?.response?.data.error_code;
              showSnackbar(
                t(`myAccount:users.errors.${errorCode}`, { userEmail: values.userEmail }),
                'error'
              );
            })
            .finally(() => {
              setIsLoadingInvite(false);
            });
        }}
      >
        {({ submitForm, values }) => (
          <InfoDrawer
            showDrawer={isFormModalOpen}
            submit={submitForm}
            isSubmitting={isLoadingInvite}
            handleClose={() => {
              setIsFormModalOpen(false);
              setCurrentlyEditing(null);
            }}
          >
            <Form trackingEvent={userInviteEvents.EDIT_FIELD}>
              <Grid container>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      fontSize: '24px',
                      fontWeight: 600,
                      background: 'linear-gradient(255.79deg, #4572B8 0%, #2FC0B8 95.35%)',
                      '-webkit-background-clip': 'text',
                      '-webkit-text-fill-color': 'transparent',
                    }}
                  >
                    {t(currentlyEditing ? 'users.modal.editTitle' : 'users.modal.createTitle')}
                  </Typography>
                  <Typography>{t('myAccount:users.modal.description')}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={!!currentlyEditing}
                    name="userFirstName"
                    label={t('user:invitation.firstName.label')}
                    placeholder={t('user:invitation.firstName.placeholder')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={!!currentlyEditing}
                    name="userLastName"
                    label={t('user:invitation.lastName.label')}
                    placeholder={t('user:invitation.lastName.placeholder')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={!!currentlyEditing}
                    name="userEmail"
                    label={t('myAccount:users.modal.userEmail.label')}
                    placeholder={t('myAccount:users.modal.userEmail.placeholder')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="jobTitle"
                    label={t('myAccount:users.modal.jobTitle.label')}
                    placeholder={t('myAccount:users.modal.jobTitle.placeholder')}
                    optional
                  />
                </Grid>
                {(!currentCompanyUser || currentlyEditing?.email !== currentCompanyUser?.user?.email) && ( // nobody can change his own role
                  <Grid item xs={12}>
                    <RadioField
                      name="role"
                      label={t('myAccount:users.modal.roles.label')}
                      options={[
                        ...(!!currentlyEditing && !currentCompanyUser
                          ? [
                              {
                                value: 'owner',
                                label: t('myAccount:users.modal.roles.options.owner.title'),
                                subLabel: t('myAccount:users.modal.roles.options.owner.description'),
                                disabled:
                                  members.some(({ role }) => role === 'owner') &&
                                  currentlyEditing.role !== 'owner',
                              },
                            ]
                          : []),
                        {
                          value: 'administrator',
                          label: t('myAccount:users.modal.roles.options.administrator.title'),
                          subLabel: t('myAccount:users.modal.roles.options.administrator.description'),
                        },
                        {
                          value: 'collaborator',
                          label: t('myAccount:users.modal.roles.options.collaborator.title'),
                          subLabel: t('myAccount:users.modal.roles.options.collaborator.description'),
                        },
                        {
                          value: 'visualizer',
                          label: t('myAccount:users.modal.roles.options.visualizer.title'),
                          subLabel: t('myAccount:users.modal.roles.options.visualizer.description'),
                        },
                      ]}
                    />
                  </Grid>
                )}
              </Grid>
            </Form>
          </InfoDrawer>
        )}
      </Formik>
      <Modal
        isAutoHeight
        isOpen={!!currentlyDeleting}
        handleClose={() => {
          setCurrentlyDeleting(null);
        }}
      >
        {currentlyDeleting && (
          <Grid container>
            <Grid item>
              <Typography>
                {t('myAccount:users.confirmationModal.description', {
                  name: `${currentlyDeleting.firstName} ${currentlyDeleting.lastName}`,
                  email: currentlyDeleting.email,
                })}
              </Typography>
            </Grid>
            <Grid item sx={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
              <Button
                type="outline"
                color="danger"
                onClick={() => {
                  setCurrentlyDeleting(null);
                }}
              >
                {t('myAccount:users.confirmationModal.cancelBtn')}
              </Button>
              <Button
                color="danger"
                isLoading={isDeleting}
                onClick={() => {
                  const _deleteMember = !!currentCompanyUser ? deleteMember : adminDeleteCompanyUser;
                  setIsDeleting(true);
                  _deleteMember(companyId, currentlyDeleting.userRoleId!).then(() => {
                    logEvent(userInviteEvents.DELETE_USER);
                    showSnackbar(t('myAccount:users.deleteMemberSuccess'), 'success');
                    removeMember(currentlyDeleting!.email);
                    setCurrentlyDeleting(null);
                    setIsDeleting(false);
                  });
                }}
              >
                {t('myAccount:users.confirmationModal.confirmBtn')}
              </Button>
            </Grid>
          </Grid>
        )}
      </Modal>
    </>
  );
};

export default UserInvites;
